<template>
  <b-form>
    <b-row align-h="center">
      <b-col cols="auto">
        <b-form-group class="mb-5"
          :label="formConfig.guardian.label" 
          label-class="h5 text-center"
          :label-for="formConfig.guardian.id"
          v-slot="{ ariaDescribedBy }"
        >
          <b-form-radio-group buttons
            class="d-flex flex-row flex-justify-around mt-4"
            button-variant="outline-dark"
            :aria-describedby="ariaDescribedBy"
            v-bind="formConfig.guardian"
            v-model="formData.guardian"
          />
        </b-form-group>
      </b-col>
    </b-row>
    <b-container v-if="formData.guardian">
      <b-row align-h="center">
        <b-col>
          <h4 class="text-center">{{ minorTitle }}</h4>
          <p class="text-center text-danger font-small font-italic">{{ minorDisclaimer }}</p>
        </b-col>
      </b-row>
      <b-row class="mb-4" align-h="center" no-gutters>
        <b-col cols="12" lg="6" xl="12" class="mb-3"
          v-for="(minor, idx) in formData.minors"
          :key="`minor-${ idx }`"
        >
          <MinorConfig 
            :minor="minor"
            ref="minors"
            v-model="formData.minors[idx]"
          >
            <b-button variant="danger" 
              @click="removeMinor(idx)" 
            >
              <b-icon v-bind="formConfig.minors.actions.remove" 
                font-scale="1.25" 
              />
            </b-button>
          </MinorConfig>
        </b-col>
      </b-row>
      <b-row align-h="center" class="my-2">
        <b-col cols="auto">
          <b-button v-bind="formConfig.minors.actions.add"
            @click="handleClick"
          >
            <!-- :disabled="!allMinorsValid" -->
            {{ formConfig.minors.actions.add.text }}
          </b-button>
        </b-col>
      </b-row>
    </b-container>
  </b-form>
</template>

<script>
import { mapState } from 'vuex';
import MinorConfig from '@/components/Waiver/MinorConfig';
import { SELECTED_RESERVATION } from '@/store/state';
import { UPDATE_GUARDIAN, UPDATE_MINORS } from '@/store/mutations';

export const GUARDIAN_EVENTS = {
  COMPLETED: 'GuardianStep:Completed',
}

const MINOR_MODEL = {
  isPlayer: true,
  first_name: null,
  last_name: null,
  date_of_birth: null
}

export default {
  components: {
    MinorConfig,
  },
  props: {
    guardian: {
      type: Boolean,
      default: () => false
    },
    minors: {
      type: Array,
      default: () => ([])
    }
  },
  data () {
    return {
      formConfig: {
        guardian: {
          id: 'guardian-select',
          label: 'Are you signing the waiver for any minors playing the game?',
          name: 'guardian-select',
          options: [
            {
              text: 'Yes',
              value: true,
            },
            {
              text: 'No',
              value: false,
            },
          ],
          required: true,
        },
        minors: {
          disclaimer: 'Minors should be between 8 and 18 years old',
          actions: {
            add: {
              text: 'Add Another Minor',
              type: 'button',
              variant: 'outline-dark',
            },
            remove: {
              icon: 'trash-fill',
              variant: 'light',
            }
          }
        },
      },
      formData: {
        guardian: this.guardian,
        minors: this.minors ?? [],
      },
      minorTitle: 'Minors'
    }
  },
  computed: {
    ...mapState([
      SELECTED_RESERVATION,
    ]),
    allMinorsValid () {
      const numValidMinors = this.formData.minors.reduce((acc, { first_name, last_name, date_of_birth }) => (
        (first_name && last_name && date_of_birth) ? acc += 1 : acc
      ), 0);

      return numValidMinors === this.formData.minors.length;
    },
    canProceed () {
      const { guardian } = this.formData;
      return guardian ? this.allMinorsValid : !guardian;
    },
    minorDisclaimer () {
      const missionId = Number(this[SELECTED_RESERVATION]?.mission_id);
      return [0, 3, 16, 18, 19].includes(missionId)
        ? 'Minors should be between 6 and 18 years old. Children under 8 years old may only play Arcade Battle and the Slime Bomb Mission (and cannot be blasted with the Paint Bomb or Foam Bomb).'
        : this.formConfig.minors.disclaimer;
    },
  },
   mounted () {
    if (this.canProceed) this.completeStep(true)
    if (this.guardian && !this.formData.minors.length) this.addMinor()
  },
  watch: {
    canProceed (fresh) {
      this.completeStep(fresh)
    },
    'formData.guardian' (fresh) {
      if (fresh && !this.formData.minors.length) {
        this.$store.commit(UPDATE_GUARDIAN, fresh)
        this.addMinor();
      }
    },
    'formData.minors' (fresh) {
      this.$store.commit(UPDATE_MINORS, fresh);
    }
  },
  methods: {
    completeStep (isComplete) {
      this.$emit(GUARDIAN_EVENTS.COMPLETED, isComplete ? this : null);
    },
    addMinor () {
      const minorInstance = { ...MINOR_MODEL };
      this.formData.minors.push(minorInstance);
    },
    handleClick () {
      const allValid = this.validateMinors()
      if (allValid) {
        this.addMinor()
      }
    },
    removeMinor (toRemove) {
      this.formData.minors.splice(toRemove, 1);
    },
    validateMinors () {
      return this.$refs.minors?.reduce((acc, m) => {
        const isValid = m.validateForm()
        return !acc ? acc : isValid 
      }, true)
    },
  },
  beforeRouteLeave (to, from, next) {
    this.validateMinors()
    if (this.canProceed || to.name === 'Promotion') {
      return next()
    }
    return false
  }
}
</script>

<style>
.btb-minor-delimiter {
  display: block;
  width: 60%; 
  margin-left: auto;
  margin-right: auto;
  text-align: center;
}
</style>